import { TreeInstance } from '@headless-tree/core';
import { addDays, endOfDay, startOfWeek } from 'date-fns';
import {
  buildDateUpdateTasks,
  getSharedDateValue,
  useUpdateTasks,
} from 'features/tasks';
import React, { useRef } from 'react';
import { InternalTreeItem } from 'shared/components/ui/virtualized-tree-list';
import { weekdaysNumberMap } from 'shared/constants';
import { useToday } from 'shared/contexts/today';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { useUser } from 'shared/hooks/use-user';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

enum DateShortcutKey {
  Today = 'KeyT',
  Unset = 'KeyU',
  NextWeek = 'KeyW',
}

const dateShortcutKeys = Object.values(DateShortcutKey).map((key) => [key]);

const dateFormatter: Record<
  DateShortcutKey,
  (
    today: Timestamp,
    { weekStartsOn }: { weekStartsOn: WeekDays; tomorrow?: boolean },
  ) => Timestamp | undefined
> = {
  [DateShortcutKey.Today]: (today, { tomorrow }) =>
    endOfDay(tomorrow ? addDays(today, 1) : today),
  [DateShortcutKey.Unset]: () => undefined,
  [DateShortcutKey.NextWeek]: (today, { weekStartsOn }) =>
    endOfDay(
      startOfWeek(addDays(today, 7), {
        weekStartsOn: weekdaysNumberMap[weekStartsOn],
      }),
    ),
};

export const useTaskDateShortcuts = (
  tree: TreeInstance<InternalTreeItem> | undefined,
  tasks: Task[],
  updateTasks: ReturnType<typeof useUpdateTasks>['submit'],
) => {
  const today = useToday();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);

  const handleDateShortcut = React.useCallback(
    (e: KeyboardEvent, keys: string[]) => {
      if (!e.shiftKey || !tree) {
        return;
      }

      const activeKey = keys.find(
        (key) => dateFormatter[key as DateShortcutKey],
      );
      if (!activeKey) {
        return;
      }

      // We need to fetch the latest selected items fresh
      const selectedItems = [...tree.getSelectedItems()];
      if (!selectedItems.length) {
        return;
      }

      const tasksMap = new Map(tasks.map((task) => [task.id, task]));

      const tasksToUpdateList = selectedItems
        .map((item) => tasksMap.get(item.getItemData().sourceId))
        .filter(Boolean) as Task[];

      e.preventDefault();
      e.stopPropagation();

      const date = dateFormatter[activeKey as DateShortcutKey](today, {
        weekStartsOn,
        tomorrow: e.altKey,
      });

      const sharedDateValue = getSharedDateValue(tasksToUpdateList);
      const tasksToUpdate = buildDateUpdateTasks(
        tasksToUpdateList,
        date,
        sharedDateValue,
        {
          weekStartsOn,
          today,
        },
      );

      tasksToUpdate.length && updateTasks(tasksToUpdate);
    },
    [tasks, tree, updateTasks, today, weekStartsOn],
  );

  // We use a ref to the handler to ensure the callback passed to useKeysDown is stable
  const handleDateShortcutRef = useRef(handleDateShortcut);
  handleDateShortcutRef.current = handleDateShortcut;

  const stableHandleDateShortcut = React.useCallback(
    (e: KeyboardEvent, keys: string[]) =>
      handleDateShortcutRef.current(e, keys),
    [],
  );

  useKeysDown(dateShortcutKeys, stableHandleDateShortcut, {
    ignoreInputFields: true,
    otherKeysInCombinationAllowed: true,
  });
};
